import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, of, throwError, forkJoin, Subject } from 'rxjs';
import { Balance, Balances, CurrencyBalance } from '../models/balance';
import { switchMap, map, share, catchError, shareReplay } from 'rxjs/operators';
import { CurrenciesService } from './currencies.service';
import {find as _find, map as _map, sortBy as _sortBy, keyBy as _keyBy, sumBy as _sumBy, clone as _clone} from 'lodash-es';
import { Currency, CurrencyType } from '../models/currency';

@Injectable({
  providedIn: 'root'
})
export class BalanceService {
  static readonly CACHE_DURATION = 1000 * 60; // 1 minute

  private refreshSubject = new Subject();

  constructor(private api: ApiService, private currenciesService: CurrenciesService) { }

  request: Observable<Balances>;

  //cached balance
  cacheExchangeHashTime: Date;
  cacheExchangeHashRequest: Observable<{[key: string]: CurrencyBalance}>;

  get refreshChanges() {
    return this.refreshSubject.asObservable();
  }

  refresh() {
    this.refreshSubject.next();
  }

  get(): Observable<Balances> {
    if (!this.request) {
      this.request = this.api.get(`{
          balance{
            wallet{
              amount{
                amount
                currency
              }
              usdValue{
                amount
                currency
              }
              value{
                amount
                currency
              }
            }
            exchange{
              amount{
                amount
                currency
              }
              usdValue{
                amount
                currency
              }
              value{
                amount
                currency
              }
            }
          }
        }`).pipe(
        switchMap(response => {
          this.request = null;
          const data = response['data'] ? response['data']['balance'] : null;
          const errors = response['errors'];

          if (data && errors &&
            ((errors[0].extensions && errors[0].extensions.category === 'forbidden')
              || (errors[0].category === 'forbidden' /* deprecated */))
          ) {
            return of({
              wallet: data['wallet'] ? data['wallet'] : [],
              exchange: data['exchange'] ? data['exchange'] : []
            });
          } else if (errors) {
            return throwError(response);
          }

          return of(response['data']['balance']);
        }),
        catchError(err => {
          this.request = null;
          return throwError(err);
        }),
        this.api.unifyErrors(),
        share()
      );
    }

    return this.request;
  }

  getWallet(): Observable<{currency: Currency, balance: Balance}> {
    return forkJoin([
      this.currenciesService.getCrypto(),
      this.get()
    ]).pipe(
      map(response => {
        const currency = _find(response[0], item => item.code === 'BTC');
        const balance = response[1].wallet && response[1].wallet.length > 0 ? response[1].wallet[0] : null;

        return {
            currency: currency,
            balance: balance
        };
      })
    );
  }

  // tslint:disable-next-line: max-line-length
  getAllWithType(currencyObservable: Observable<Currency[]>): Observable<{wallet: CurrencyBalance, exchange: CurrencyBalance[] }> {
    return forkJoin([
      currencyObservable,
      this.get()
    ]).pipe(
      map(response => {
        const balances = _keyBy(response[1].exchange, balance => balance.amount.currency);
        const exchange = _map(response[0], val => {
          return {
            currency: val,
            balance: balances[val.code]
          };
        });

        const walletCurrency = _clone(_find(response[0], item => item.code === 'BTC'));
        const walletBalance = response[1].wallet && response[1].wallet.length > 0 ? response[1].wallet[0] : null;


        return {
          wallet: {
            currency: walletCurrency,
            balance: walletBalance
          },
          exchange
        };
      })
    );
  }

  getExchangeWithType(currencyObservable: Observable<Currency[]>): Observable<{currency: Currency, balance: Balance}[]> {
    return forkJoin([
      currencyObservable,
      this.get()
    ]).pipe(
      map(response => {
        const balances = _keyBy(response[1].exchange, balance => balance.amount.currency);

        return _map(response[0], (val) => {
          return {
            currency: val,
            balance: balances[val.code]
          };
        });
      })
    );
  }

  getAll(): Observable<{wallet: CurrencyBalance, exchange: CurrencyBalance[] }> {
    return this.getAllWithType(this.currenciesService.getAllSorted());
  }

  getFiat(): Observable<{currency: Currency, balance: Balance}[]> {
    return this.getExchangeWithType(this.currenciesService.getFiat());
  }

  getExchange(): Observable<{currency: Currency, balance: Balance}[]> {
    return this.getExchangeWithType(this.currenciesService.getAllSorted());
  }

  getExchangeCrypto(): Observable<{currency: Currency, balance: Balance}[]> {
    return this.getExchangeWithType(this.currenciesService.getCrypto());
  }

  getExchangeHash(): Observable<{[key: string]: CurrencyBalance}> {
    return this.getExchange().pipe(
      map(response => {
        return _keyBy(response, item => item.currency.code);
      })
    );
  }

  getFiatBalance(): Observable<Balance> {
    return this.get().pipe(
      switchMap(response => {
        // TODO: use ES6 rather than lodash
        return of(_find(response.exchange, (balance) => balance.currency.type === CurrencyType.Fiat && balance.currency.acceptedPairs != null));
      })
    );
  }

  getExchangeTotal(): Observable<number> {
    return this.getExchange().pipe(
      map(response => {
        if (response) {
          return _sumBy(response, item => item.balance && item.balance.value ? parseFloat(item.balance.value.amount) : 0);
        } else {
          return 0;
        }
      })
    );
  }

  getCachedExchangeBalances(forceRefresh = false): Observable<{[key: string]: CurrencyBalance}> {
    if (!forceRefresh && this.cacheExchangeHashRequest && !this.isCacheExpired()) {
      return this.cacheExchangeHashRequest;
    }

    this.cacheExchangeHashTime = new Date();
    this.cacheExchangeHashRequest = this.getExchangeHash().pipe(shareReplay());
    return this.cacheExchangeHashRequest;
  }

  getCurrencyBalance(currency: string, forceRefresh = false) {
    if (forceRefresh) {
      this.cacheExchangeHashRequest = null;
    }

    return this.getCachedExchangeBalances().pipe(
      map(response => {
        return response[currency]?.balance;
      })
    );
  }

  isCacheExpired() {
    if (!this.cacheExchangeHashTime) {
      return true;
    }

    const now = new Date();
    const diff = now.getTime() - this.cacheExchangeHashTime.getTime();
    if (diff > BalanceService.CACHE_DURATION) {
      return true;
    }

    return false;
  }

  clearCache() {
    this.cacheExchangeHashRequest = null;
  }

  sortByValue(balances: CurrencyBalance[], preferredCurrency: string = null): CurrencyBalance[] {
    return _sortBy(balances, [
      item => item.currency.type === CurrencyType.Fiat ? 1 : 0,
      item => item.balance && item.balance.value ? parseFloat(item.balance.value.amount) : 0,
      item => item.balance && item.balance.amount ? parseFloat(item.balance.amount.amount) : 0,
      item => item.currency.code === preferredCurrency ? 1 : 0,
      item => -1 * item.currency.sortOrder
    ]).reverse();
  }

  removeZeroBalances(balances: CurrencyBalance[]) {
    return balances.filter(item => item.balance);
  }
}
